import React, { useEffect, useRef, useState } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

export const DND_TYPE = "moveAgent";

/* ---------- small shared pieces (also used by the Watch live page) ---------- */

// The agent's presence: a soft orb that breathes while it's live, orbits while it works, and
// pulses while it waits on you. Motion stops under prefers-reduced-motion (see agents.scss).
export const AgentOrb = ({ mode = "rest", size = "md" }) => (
  <span className={`ag-orb ag-orb--${mode} ag-orb--${size}`} aria-hidden="true">
    {mode === "working" && <span className="ag-orb__orbit" />}
    {mode === "waiting" && <span className="ag-orb__ring" />}
    <span className="ag-orb__core" />
  </span>
);

export const orbMode = state =>
  ({ RUNNING: "working", WAITING_APPROVAL: "waiting", FAILING: "failing", ACTIVE: "live" }[state] || "rest");

export const timeAgo = ts => {
  if (!ts) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

// "Today 8:00 AM" / "Tomorrow 8:00 AM" / "Mon 8:00 AM", in the viewer's clock.
export const whenLabel = ts => {
  if (!ts) return "";
  const d = new Date(ts);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((day - today) / 86400000);
  if (diff === 0) return `today ${time}`;
  if (diff === 1) return `tomorrow ${time}`;
  return `${d.toLocaleDateString([], { weekday: "short" })} ${time}`;
};

// "Today, 6:11 PM" / "Yesterday, 9:02 AM" / "28 Sep, 8:00 AM" for the runs table.
// ICP searches can carry a date when the plain name was taken: older "Agent — ICP (30/09/2026, 18:11:42)",
// newer "Agent ICP, 7 Oct 10:00 AM" / "Agent ICP, Oct 7 10:00 AM". Show just the name.
export const cleanSearchName = n => String(n || "")
  .replace(/\s*\(\d{1,2}\/\d{1,2}\/\d{4},?[^)]*\)\s*$/, "")
  .replace(/,\s+(\d{1,2}\s+[A-Za-z]{3,}|[A-Za-z]{3,}\s+\d{1,2})\s+\d{1,2}:\d{2}(\s*[ap]\.?\s?m\.?)?\s*$/i, "")
  .replace(/\s+[\u2014\u2013-]\s+/g, " ");

export const runWhen = ts => {
  if (!ts) return "-";
  const d = new Date(ts);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - day) / 86400000);
  if (diff === 0) return `Today, ${time}`;
  if (diff === 1) return `Yesterday, ${time}`;
  return `${d.toLocaleDateString([], { day: "numeric", month: "short" })}, ${time}`;
};

// Width classes (ag-prog--0 … ag-prog--100 in steps of 5) instead of inline widths.
export const progClass = (done, total) =>
  `ag-prog--${total > 0 ? Math.min(100, Math.round((done / total) * 20) * 5) : 0}`;

// The steps a person would recognise (the digest is bookkeeping, not a step of the job).
export const visibleSteps = agent => (agent.flow || []).filter(n => n.stepType !== "notify.digest");

const TICK = {
  SUCCEEDED: "ok", PARTIAL: "partial", FAILED: "failed", CANCELLED: "off",
  WAITING_APPROVAL: "wait", SLEEPING: "wait", RUNNING: "run", QUEUED: "run"
};

export const RunTicks = ({ runs }) => (
  <span className="ag-ticks" aria-label="Last 10 runs">
    {Array.from({ length: 10 }).map((_, i) => {
      const st = (runs || [])[i - (10 - (runs || []).length)];
      return <span key={i} className={`ag-ticks__t ag-ticks__t--${st ? TICK[st] || "off" : "none"}`} title={st || ""} />;
    })}
  </span>
);

/* ---------- what the card says ---------- */

// Three numbers that fit what the agent does (decision #3: per step type, picked per template).
export const metricsFor = (agent, totals) => {
  const t = totals || {};
  const steps = t.steps || {};
  const s = type => steps[type] || 0;
  const enrolls = (agent.flow || []).some(n => n.stepType === "act.enroll_sequence");
  const done = { v: t.done || 0, l: enrolls ? "enrolled" : "processed" };
  switch (agent.templateKey) {
    case "reply_router": {
      // "Positive" = what reached the lane the sentiment branch sends positive replies down.
      const branch = (agent.flow || []).find(n => n.stepType === "branch.switch");
      const lane = branch && (branch.next || [])[0];
      const positive = lane && lane !== "__end" ? ((t.nodes || {})[lane] || 0) : null;
      const third = s("act.create_task") ? { v: s("act.create_task"), l: "tasks" }
        : s("act.enroll_sequence") ? { v: s("act.enroll_sequence"), l: "enrolled" }
        : { v: s("act.stop_sequence") + s("act.pause_sequence"), l: "stopped" };
      return positive === null
        ? [{ v: t.found || 0, l: "replies" }, third]
        : [{ v: t.found || 0, l: "replies" }, { v: positive, l: "positive" }, third];
    }
    case "lookalike_prospector":
    case "company_prospector":
      return [{ v: s("source.lookalike_companies") + s("source.find_companies"), l: "companies" },
        { v: s("expand.people_at_company"), l: "people" }, done];
    case "account_prospector":
      return [{ v: s("source.from_account_list"), l: "accounts" }, { v: s("expand.people_at_company"), l: "people" }, done];
    case "linkedin_import":
      return [{ v: t.found || 0, l: "imported" }, { v: s("enrich.find_email"), l: "verified" }, done];
    case "activity_report":
      return [{ v: t.runs || 0, l: "reports" }];
    default:
      return [{ v: t.found || 0, l: "found" }, { v: s("enrich.find_email") || s("filter.deliverable"), l: "verified" }, done];
  }
};

// What to do about a failing agent, read from the failed run's stored error and step notes only
// (no extra calls on page load). First match wins, so the specific cases come first.
export const fixFor = (agent, run) => {
  if (!run) return null;
  const text = [run.error].concat((run.steps || []).map(s => s.note)).filter(Boolean).join(" · ");
  const seq = ((agent.flow || []).find(n => n.stepType === "act.enroll_sequence") || {}).params || {};
  const rules = [
    [/credit budget reached/i, "It hit its per-run credit cap.", "Raise the cap", `/agents/${agent.id}`],
    [/linkedin[^·]*(limit|credits)/i, "LinkedIn import credits or the daily limit ran out.", "Add credits", "/settings/billing"],
    [/linkedin|sales navigator/i, "The LinkedIn account isn't connected.", "Reconnect LinkedIn", "/settings/account/linkedin"],
    [/inbox|gmail|outlook|smtp|oauth|invalid_grant|unauthori[sz]ed|token expired/i, "The inbox it sends from needs reconnecting.", "Reconnect inbox", "/settings/account/email"],
    [/slack/i, "Slack isn't connected.", "Connect Slack", "/settings/crm/slack"],
    [/hubspot|salesforce|pipedrive|zoho|\bcrm\b/i, "The CRM isn't connected.", "Reconnect CRM", "/settings/crm"],
    [/credit|quota|insufficient|limit reached/i, "Your team is out of credits.", "Add credits", "/settings/billing"],
    [/sequence/i, "Its sequence is missing or paused.", "Open sequence", seq.sequenceId ? `/sequences/all/${seq.sequenceId}` : "/sequences"],
    [/icp|saved search/i, "Its ICP search is missing.", "Fix the ICP", `/agents/${agent.id}`],
    [/worker .*stopped/i, "The run was interrupted on our side.", "Run it again", `/agents/${agent.id}`]
  ];
  const hit = rules.find(r => r[0].test(text));
  return hit ? { why: hit[1], label: hit[2], to: hit[3], detail: text } : { why: run.error || "", label: "See what failed", to: `/agents/${agent.id}`, detail: text };
};

const isIcpAgent = a => a.templateKey === "daily_icp_prospector" || (a.config && a.config.sellerProfileId);
export const needsIcp = a => isIcpAgent(a) && !(a.config && a.config.icpApproved) && a.status !== "ACTIVE";

// "Approves first run · 50 per run" — the guardrails, in a line.
export const guardrailLine = a => {
  const cfg = a.config || {};
  const mode = cfg.approvalMode === "REQUIRED" ? "Approves every run"
    : !a.trusted ? "Approves first run" : "Autopilot · no approval step";
  return isIcpAgent(a) && cfg.dailyRecordCap ? `${mode} · ${cfg.dailyRecordCap} per run` : mode;
};

const eventWord = a => {
  const t = (a.trigger && a.trigger.type) || "";
  return t.startsWith("event.") ? t.slice(6).replace(/_/g, " ").toLowerCase() : "";
};

/* ---------- the card ---------- */

/**
 * One agent on the agents page. `card` is an entry of GET /cm/agents/overview. `stepName` maps a
 * step type to its display name; `onAction(kind, card)` handles the primary button and menu.
 */
const AgentCard = ({ card, stepName, onOpen, onAction, scheduleLabel, typeLabel }) => {
  const a = card.agent || {};
  const state = card.state || a.status || "DRAFT";
  const run = card.activeRun || null;
  const last = card.lastRun || null;
  const steps = visibleSteps(a);
  // Summaries are written as each step finishes, so their count is where the run has got to.
  const doneSteps = Math.min(steps.length, ((run && run.steps) || []).length);
  const current = steps[doneSteps] || steps[steps.length - 1];
  const metrics = metricsFor(a, card.totals);
  // "12 of 40" inside the step that's running now (written by the engine as it goes).
  const inStep = run && run.stepProgress && run.stepProgress.total ? run.stepProgress : null;

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: DND_TYPE,
    item: { id: a.id, folder: a.folder || "", name: a.name },
    collect: m => ({ isDragging: m.isDragging() })
  });
  useEffect(() => { previewRef(getEmptyImage(), { captureDraggingState: true }); }, [previewRef]);

  // The menu sits inside the card, under its button (the card doesn't clip, see .ag-acard).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = e => {
      if (e && ((menuRef.current && menuRef.current.contains(e.target)) || (btnRef.current && btnRef.current.contains(e.target)))) return;
      setMenuOpen(false);
    };
    const onKey = e => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);
  const toggleMenu = e => { e.stopPropagation(); setMenuOpen(o => !o); };
  const act = kind => e => { e.stopPropagation(); setMenuOpen(false); onAction(kind, card, kind === "fix" ? fix : null); };

  const fix = state === "FAILING" ? fixFor(a, last) : null;

  // What's happening, in a sentence.
  let line;
  if (state === "RUNNING") line = null; // the progress block says it
  else if (state === "WAITING_APPROVAL") line = <>Paused before acting. <b>{card.waitingContacts} contacts</b> are waiting for your approval.</>;
  else if (state === "FAILING") line = <span title={(fix && fix.detail) || ""}>Last {card.failedInARow} runs failed.{fix && fix.why ? ` ${fix.why}` : ""}</span>;
  else if (a.status === "ACTIVE") {
    const ev = eventWord(a);
    if (ev) line = <>Waiting for the next {ev}{last ? ` · last run ${timeAgo(last.startedAt)}` : ""}</>;
    else if (card.nextRunAt) line = <>Next run {whenLabel(card.nextRunAt)}{last ? ` · last run ${timeAgo(last.startedAt)}` : ""}</>;
    else line = <>Runs when you start it{last ? ` · last run ${timeAgo(last.startedAt)}` : ""}</>;
  } else if (a.status === "PAUSED") line = <>Paused · nothing runs until you resume</>;
  else if (a.status === "ARCHIVED") line = <>Archived · restore it to use it again</>;
  else if (needsIcp(a)) line = <>One step left: approve who it should look for.</>;
  else line = <>Not switched on yet.</>;

  const primary = {
    RUNNING: { kind: "watch", label: "Watch live" },
    WAITING_APPROVAL: { kind: "review", label: `Review ${card.waitingContacts} contacts` },
    FAILING: { kind: "fix", label: (fix && fix.label) || "See what failed" },
    ACTIVE: { kind: "open", label: "Open runs" },
    PAUSED: { kind: "resume", label: "Resume" },
    ARCHIVED: { kind: "restore", label: "Restore" }
  }[state] || { kind: "open", label: "Finish setup" };

  const showMetrics = a.status !== "DRAFT" || (card.totals && card.totals.runs > 0);

  return (
    <article
      ref={dragRef}
      className={`ag-acard ag-acard--${state.toLowerCase()}${isDragging ? " is-dragging" : ""}${menuOpen ? " has-menu" : ""}`}
      onClick={() => onOpen(card)}
    >
      <span className="ag-acard__accent" />
      <div className="ag-acard__head">
        <AgentOrb mode={a.status === "DRAFT" && state !== "RUNNING" ? "draft" : orbMode(state)} />
        <div className="ag-acard__who">
          <span className="ag-acard__name" title={a.name}>{a.name || "Untitled agent"}</span>
          <span className="ag-acard__what" title={a.summary || ""}>{typeLabel(a)}{a.summary ? ` · ${a.summary}` : ""}</span>
        </div>
        {a.status !== "ACTIVE" && state !== "RUNNING" && state !== "WAITING_APPROVAL" && (
          <span className="ag-acard__tag">{{ PAUSED: "Paused", DRAFT: "Draft", ARCHIVED: "Archived" }[a.status] || ""}</span>
        )}
        <button ref={btnRef} className="ag-acard__menubtn material-icons-outlined" aria-label="More actions"
          onMouseDown={e => e.stopPropagation()} onClick={toggleMenu}>more_horiz</button>
      </div>

      {state === "RUNNING" ? (
        <div className="ag-acard__run">
          <span className="ag-acard__shimmer" />
          <div className="ag-acard__runline">
            <span className="ag-livedot" />
            <b>Running</b>
            {steps.length > 0 && <span>Step {doneSteps + 1} of {steps.length}{current ? ` · ${stepName(current.stepType)}` : ""}</span>}
            {inStep && <span className="ag-acard__count">{inStep.done} of {inStep.total}</span>}
          </div>
          <div className="ag-acard__bar"><span className={`ag-acard__fill ${progClass(doneSteps, steps.length)}`} /></div>
          <div className="ag-acard__chain">
            {steps.slice(0, 5).map((n, i) => (
              <React.Fragment key={n.nodeId || i}>
                {i > 0 && <span className="ag-acard__sep">›</span>}
                <span className={`ag-acard__chip${i < doneSteps ? " is-done" : i === doneSteps ? " is-now" : ""}`}>
                  {i === doneSteps && <span className="ag-livedot" />}{stepName(n.stepType)}
                </span>
              </React.Fragment>
            ))}
            {steps.length > 5 && <span className="ag-acard__sep">+{steps.length - 5}</span>}
          </div>
        </div>
      ) : (
        <div className="ag-acard__line">
          {(state === "ACTIVE" || state === "WAITING_APPROVAL" || state === "FAILING") && <span className="ag-acard__linedot" />}
          <span>{line}</span>
        </div>
      )}

      {showMetrics && (
        <div className="ag-acard__nums">
          {metrics.map(m => (
            <div key={m.l} className="ag-acard__num"><b>{m.v}</b><span>{m.l}</span></div>
          ))}
          <span className="ag-acard__window">last 14 days</span>
        </div>
      )}

      <div className="ag-acard__foot1">
        {a.status !== "DRAFT" && <RunTicks runs={card.recentRuns} />}
        <span className="ag-acard__sched">
          {state === "RUNNING" && run ? `Started ${timeAgo(run.startedAt)} · ${scheduleLabel(a)}` : scheduleLabel(a)}
        </span>
      </div>

      <div className="ag-acard__foot">
        <span className="ag-acard__guard">{a.status === "DRAFT" ? "Not switched on yet" : guardrailLine(a)}</span>
        <button className={`ag-btn ag-btn--sm${state === "RUNNING" || state === "WAITING_APPROVAL" || a.status === "DRAFT" ? " ag-btn--primary" : ""}`}
          onClick={act(primary.kind)}>
          <i className="material-icons-outlined">{{ watch: "visibility", review: "assignment_turned_in", fix: "build", resume: "play_arrow", restore: "unarchive" }[primary.kind] || (a.status === "DRAFT" ? "arrow_forward" : "history")}</i>
          {primary.label}
        </button>
      </div>

      {menuOpen && (
        <div ref={menuRef} className="ag-menu ag-acard__menu" onClick={e => e.stopPropagation()}>
          <button className="ag-menu__item" onClick={act("open")}><i className="material-icons-outlined">open_in_new</i>Open</button>
          <button className="ag-menu__item" onClick={act("rename")}><i className="material-icons-outlined">edit</i>Rename</button>
          {a.status === "ACTIVE" && <button className="ag-menu__item" onClick={act("pause")}><i className="material-icons-outlined">pause</i>Pause</button>}
          {a.status === "ARCHIVED"
            ? <button className="ag-menu__item" onClick={act("restore")}><i className="material-icons-outlined">unarchive</i>Restore</button>
            : <button className="ag-menu__item" onClick={act("archive")}><i className="material-icons-outlined">archive</i>Archive</button>}
          <button className="ag-menu__item ag-menu__item--danger" onClick={act("delete")}>
            <i className="material-icons-outlined">delete_outline</i>Delete</button>
        </div>
      )}
    </article>
  );
};

export default AgentCard;
